import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2, Paperclip, X } from "lucide-react";

const PROJECT_TYPES = [
    "Brand Activation",
    "Private Event",
    "Product Launch",
    "Pop-Up",
    "Set / Stage",
    "Venue Planning",
    "Bespoke Bar / Feature",
    "Other",
];

const BUDGET_RANGES = [
    "Under £500",
    "£500 – £1,000",
    "£1,000 – £2,500",
    "£2,500 – £5,000",
    "£5,000+",
    "Not sure yet",
];

export default function Contact() {
    const fileRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        project_type: "",
        event_date: "",
        venue: "",
        visualise_what: "",
        budget_range: "",
        message: "",
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onFilesPicked = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploadingFiles(true);
        const uploaded = [...attachments];
        try {
            for (const file of files) {
                if (file.size > 25 * 1024 * 1024) {
                    toast.error(`${file.name} exceeds 25MB`);
                    continue;
                }
                const data = new FormData();
                data.append("file", file);
                const r = await axios.post(`${API}/inquiries/upload`, data, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploaded.push({ ...r.data });
            }
            setAttachments(uploaded);
            toast.success("Files uploaded");
        } catch (err) {
            toast.error("Upload failed — please try again");
        } finally {
            setUploadingFiles(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removeFile = (idx) => {
        setAttachments((a) => a.filter((_, i) => i !== idx));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Name, email and a short message are required");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/inquiries`, {
                ...form,
                attachments,
            });
            toast.success(
                "Thank you — your enquiry has been received. I'll be in touch shortly."
            );
            setForm({
                name: "",
                company: "",
                email: "",
                phone: "",
                project_type: "",
                event_date: "",
                venue: "",
                visualise_what: "",
                budget_range: "",
                message: "",
            });
            setAttachments([]);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not send enquiry — please try again or email directly."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="section-pad bg-navy-deep text-background"
        >
            <div className="container-edge">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <h2
                            data-testid="contact-heading"
                            className="font-display text-4xl md:text-5xl lg:text-[56px] tracking-tight font-medium leading-[1.04] text-balance"
                        >
                            Have an event idea that{" "}
                            <span className="italic font-normal text-champagne">
                                needs visualising?
                            </span>
                        </h2>
                        <p className="mt-8 text-background/75 text-[15px] leading-relaxed max-w-md">
                            Send over your brief, venue plan, sketch, reference
                            images or initial idea and I can help turn it into clear,
                            client-ready visual material.
                        </p>

                        <div className="mt-10 space-y-5 text-[14px]">
                            <div>
                                <p className="overline !text-background/60">Email</p>
                                <a
                                    href="mailto:alfie@alfiecarter.org"
                                    data-testid="contact-email"
                                    className="mt-1 inline-block text-background hover:opacity-70 transition-opacity"
                                >
                                    alfie@alfiecarter.org
                                </a>
                            </div>
                            <div>
                                <p className="overline !text-background/60">Phone</p>
                                <a
                                    href="tel:+447861762191"
                                    data-testid="contact-phone"
                                    className="mt-1 inline-block text-background hover:opacity-70 transition-opacity"
                                >
                                    07861 762 191
                                </a>
                            </div>
                            <div>
                                <p className="overline !text-background/60">Based</p>
                                <p className="mt-1 text-background/85">
                                    London, working with event teams across the UK.
                                </p>
                            </div>
                            <div>
                                <p className="overline !text-background/60">
                                    Typical response
                                </p>
                                <p className="mt-1 text-background/85">
                                    Within 1 working day.
                                </p>
                            </div>
                        </div>
                    </div>

                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2"
                    >
                        <Field label="Name *" testId="field-name">
                            <input
                                required
                                value={form.name}
                                onChange={(e) => update("name", e.target.value)}
                                data-testid="input-name"
                                className="field-line !text-background placeholder:!text-background/40 !border-background/30 focus:!border-background"
                                placeholder="Your full name"
                            />
                        </Field>
                        <Field label="Company" testId="field-company">
                            <input
                                value={form.company}
                                onChange={(e) => update("company", e.target.value)}
                                data-testid="input-company"
                                className="field-line !text-background placeholder:!text-background/40 !border-background/30 focus:!border-background"
                                placeholder="Agency, brand, venue (optional)"
                            />
                        </Field>
                        <Field label="Email *" testId="field-email">
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                data-testid="input-email"
                                className="field-line !text-background placeholder:!text-background/40 !border-background/30 focus:!border-background"
                                placeholder="you@email.com"
                            />
                        </Field>
                        <Field label="Phone" testId="field-phone">
                            <input
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                data-testid="input-phone"
                                className="field-line !text-background placeholder:!text-background/40 !border-background/30 focus:!border-background"
                                placeholder="+44 …"
                            />
                        </Field>

                        <Field label="Project type" testId="field-project-type">
                            <select
                                value={form.project_type}
                                onChange={(e) =>
                                    update("project_type", e.target.value)
                                }
                                className="field-line !text-background !bg-navy-deep !border-background/30 focus:!border-background"
                                data-testid="input-project-type"
                            >
                                <option value="">Select…</option>
                                {PROJECT_TYPES.map((t) => (
                                    <option key={t} value={t}>
                                        {t}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Event date / deadline" testId="field-date">
                            <input
                                value={form.event_date}
                                onChange={(e) =>
                                    update("event_date", e.target.value)
                                }
                                data-testid="input-event-date"
                                className="field-line !text-background placeholder:!text-background/40 !border-background/30 focus:!border-background"
                                placeholder="e.g. 14 March 2026"
                            />
                        </Field>

                        <Field label="Venue / location" testId="field-venue" full>
                            <input
                                value={form.venue}
                                onChange={(e) => update("venue", e.target.value)}
                                data-testid="input-venue"
                                className="field-line !text-background placeholder:!text-background/40 !border-background/30 focus:!border-background"
                                placeholder="Venue name, postcode or area"
                            />
                        </Field>

                        <Field label="What do you need visualised?" testId="field-visualise" full>
                            <input
                                value={form.visualise_what}
                                onChange={(e) =>
                                    update("visualise_what", e.target.value)
                                }
                                data-testid="input-visualise"
                                className="field-line !text-background placeholder:!text-background/40 !border-background/30 focus:!border-background"
                                placeholder="Layout, scenic feature, pitch visuals…"
                            />
                        </Field>

                        <Field label="Budget range" testId="field-budget" full>
                            <select
                                value={form.budget_range}
                                onChange={(e) =>
                                    update("budget_range", e.target.value)
                                }
                                className="field-line !text-background !bg-navy-deep !border-background/30 focus:!border-background"
                                data-testid="input-budget"
                            >
                                <option value="">Select…</option>
                                {BUDGET_RANGES.map((b) => (
                                    <option key={b} value={b}>
                                        {b}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Message *" testId="field-message" full>
                            <textarea
                                required
                                rows={4}
                                value={form.message}
                                onChange={(e) => update("message", e.target.value)}
                                data-testid="input-message"
                                className="field-line !text-background placeholder:!text-background/40 !border-background/30 focus:!border-background resize-none"
                                placeholder="Tell me about the project, the space, and the level of detail you need…"
                            />
                        </Field>

                        {/* File upload */}
                        <div className="md:col-span-2 mt-4 border border-background/25 p-5">
                            <div className="flex items-center justify-between">
                                <p className="overline !text-background/60">
                                    Upload brief / plans / references
                                </p>
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    disabled={uploadingFiles}
                                    data-testid="contact-upload-trigger"
                                    className="text-[12px] tracking-[0.1em] uppercase border border-background/30 px-3 py-2 hover:bg-background hover:text-foreground transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                                >
                                    {uploadingFiles ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                        <Paperclip size={14} />
                                    )}
                                    {uploadingFiles ? "Uploading…" : "Add files"}
                                </button>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    hidden
                                    multiple
                                    onChange={onFilesPicked}
                                    data-testid="contact-file-input"
                                    accept="image/*,application/pdf,.dwg,.skp,.doc,.docx,.zip"
                                />
                            </div>

                            {attachments.length > 0 && (
                                <ul className="mt-4 space-y-2">
                                    {attachments.map((a, i) => (
                                        <li
                                            key={a.storage_path}
                                            className="flex items-center justify-between text-[13px] border-b border-background/15 pb-2"
                                        >
                                            <span className="truncate pr-3 text-background/90">
                                                {a.original_filename}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeFile(i)}
                                                data-testid={`contact-file-remove-${i}`}
                                                className="text-background/60 hover:text-background"
                                                aria-label={`Remove ${a.original_filename}`}
                                            >
                                                <X size={14} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <p className="mt-3 text-[11px] text-background/50">
                                PDFs, images, plans or sketches up to 25MB each.
                            </p>
                        </div>

                        <div className="md:col-span-2 mt-8 flex flex-col md:flex-row md:items-center gap-4">
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit"
                                className="inline-flex items-center justify-center gap-2 rounded-none border border-background bg-background text-foreground hover:bg-transparent hover:text-background transition-all duration-300 px-8 py-4 text-[13px] font-medium tracking-[0.05em] uppercase disabled:opacity-60"
                            >
                                {submitting ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <ArrowRight size={16} />
                                )}
                                {submitting ? "Sending…" : "Start a Project"}
                            </button>
                            <p className="text-[12px] text-background/55 max-w-xs">
                                By submitting, you agree to be contacted about your
                                enquiry. Files are stored securely.
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, children, full, testId }) {
    return (
        <div
            data-testid={testId}
            className={`${full ? "md:col-span-2" : ""} pt-6 pb-2`}
        >
            <label className="overline !text-background/60 block mb-1">
                {label}
            </label>
            {children}
        </div>
    );
}
